<script lang="ts">
    import { untrack } from 'svelte';
    import { log } from '@shared/log.browser.ts';
    import TileItemObserver from '@tilelist/components/product-tile-item/observation/TileItemObserver.svelte';
    import { tileObserver } from '@tilelist/components/product-tile-item/observation/TileObserver.browser.ts';
    import { brandTileAttributes } from '@tilelist/domain/external-data-attributes/ExternalDataAttributes.ts';
    import type { BrandTileCampaign } from '@tilelist/domain/feature-visibility/BrandTile.ts';

    type Props = {
        campaign: BrandTileCampaign;
        onSeen: () => Promise<void>;
        onTileClick: (event: Event) => void;
        cacheRestored: boolean;
        localListPosition: number;
        actualListPosition: number;
        featureIndex?: number;
        rule?: string;
    };

    let {
        cacheRestored,
        campaign,
        featureIndex,
        rule,
        onSeen,
        onTileClick,
        localListPosition,
        actualListPosition,
    }: Props = $props();

    const backgroundClassName = $derived.by(() => {
        switch (campaign.backgroundToken) {
            case 'color/background/strong/warm-red':
                return 'reptile-brand-tile--strong-warm-red';
            case 'color/background/strong/purple':
                return 'reptile-brand-tile--strong-purple';
            case 'color/background/strong/blue':
                return 'reptile-brand-tile--strong-blue';
            case 'color/beige/140':
                return 'reptile-brand-tile--beige-140';
        }
    });

    let seen = $state(false);
    let itemTrackingAttachment = $state();

    let lastContextRule = $state<string | undefined>(untrack(() => rule));
    $effect(() => {
        if (rule !== lastContextRule) {
            lastContextRule = rule;
            seen = false;
        }
    });

    async function handleSeen() {
        try {
            await onSeen();
            seen = true;
        } catch (error) {
            log.warn('Failed to track tile visibility:', error);
        }
    }
</script>

<article
    {@attach itemTrackingAttachment}
    class={['reptile-brand-tile', backgroundClassName]}
    data-list-position={actualListPosition}
    data-local-list-position={localListPosition}
    data-qa="reptile-brand-tile"
    {...brandTileAttributes(featureIndex)}
>
    <!-- Tracking observer: triggers when tile is fully visible -->
    <TileItemObserver
        action={handleSeen}
        successful={seen}
        {cacheRestored}
        observe={tileObserver.observeOnEnter.bind(tileObserver)}
        onAttachment={(a) => (itemTrackingAttachment = a)}
    />

    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <oc-link-v2
        class="reptile-brand-tile__link"
        base64-href={btoa(campaign.linkUrl)}
        onclick={onTileClick}
        oc-aria-label={`${campaign.brand} – ${campaign.text}`}
    ></oc-link-v2>

    <div class="reptile-brand-tile__image-wrapper">
        <img
            class="reptile-brand-tile__image"
            src={campaign.image.src}
            alt={campaign.image.alt}
            loading="lazy"
            decoding="async"
            style:object-position={campaign.image.objectPosition ?? 'center'}
            style:transform={campaign.image.transform ?? ''}
        />
    </div>

    <div class="reptile-brand-tile__info">
        <span class="oc-copy-75 reptile-brand-tile__brand">{campaign.brand}</span>
        <span class="oc-headline-100 reptile-brand-tile__text">{campaign.text}</span>
        <div class="reptile-brand-tile__sponsored-hint">
            <ofc-sponsored-tag-v1
                advertiser-legal-name={campaign.advertiserLegalName}
                funder-legal-name={campaign.funderLegalName}
                link-variant="inverted"
            ></ofc-sponsored-tag-v1>
        </div>
    </div>
</article>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';

    .reptile-brand-tile {
        overflow: hidden;
        border-radius: tokens.$oc-base-dimension-absolute-4;
        position: relative;
        display: flex;
        flex-direction: column;

        &--strong-warm-red {
            background-color: tokens.$oc-semantic-color-background-strong-warm-red;
        }

        &--strong-purple {
            background-color: tokens.$oc-semantic-color-background-strong-purple;
        }

        &--strong-blue {
            background-color: tokens.$oc-semantic-color-background-strong-blue;
        }

        &--beige-140 {
            background-color: tokens.$oc-base-color-beige-140;
        }

        &__info {
            display: flex;
            flex-direction: column;
            gap: tokens.$oc-semantic-spacing-absolute-50;
            padding: tokens.$oc-semantic-spacing-relative-50 tokens.$oc-semantic-spacing-absolute-100
                tokens.$oc-semantic-spacing-absolute-100;
        }

        &__sponsored-hint {
            position: relative;
            z-index: 2;

            /**
 *
 */
            oc-link-v2 {
                padding-bottom: 0 !important;
            }
        }

        &__link {
            position: absolute;
            inset: 0;
            z-index: 1;
            display: block;
        }

        &__image-wrapper {
            flex: 1;
            min-height: 0;
            margin: tokens.$oc-semantic-spacing-relative-50;
            overflow: hidden;
            border-radius: tokens.$oc-semantic-border-radius-100;
        }

        &__image {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        &__brand,
        &__text {
            color: tokens.$oc-semantic-color-text-inverted;
            margin: 0;
        }

        &__brand {
            line-height: 1.2;
        }
    }
</style>
